import { seed } from '../data/db';
import type { ActionLevel, AgentId, HouseMode, Processing } from '../types/anna';
import type { AgentTask, LogEntry, Meta, SlackMessage, Surface, Tables } from '../types/db';
import type { AnnaSettingsState, MemoryKind } from '../types/settings';

/**
 * Fake in-memory database. Seeded from data/db, mutated through the methods
 * below, observed through hooks/useDb. Every action Anna takes writes a log row.
 */
let state: Tables = structuredClone(seed);
const listeners = new Set<() => void>();
let logSeq = 100;

function commit(next: Tables) {
  state = next;
  listeners.forEach((fn) => fn());
}

function entry(e: {
  agent: AgentId | 'systeem';
  summary: string;
  level: ActionLevel;
  where: Processing;
  outcome: LogEntry['outcome'];
  traceId?: string;
}): LogEntry {
  return { ...e, id: `l-${logSeq++}`, time: state.meta.time };
}

export const db = {
  getState: (): Tables => state,

  subscribe(fn: () => void): () => void {
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  },

  setMeta(patch: Partial<Meta>) {
    commit({ ...state, meta: { ...state.meta, ...patch } });
  },

  log(e: Parameters<typeof entry>[0]) {
    commit({ ...state, log: [...state.log, entry(e)] });
  },

  approveProposal(id: string) {
    const p = state.proposals.find((x) => x.id === id);
    if (!p || p.status !== 'wacht') return;
    commit({
      ...state,
      proposals: state.proposals.map((x) => (x.id === id ? { ...x, status: 'goedgekeurd' } : x)),
      pullRequests:
        p.kind === 'code'
          ? state.pullRequests.map((pr) => (p.refIds.includes(pr.id) ? { ...pr, status: 'goedgekeurd' } : pr))
          : state.pullRequests,
      agentTasks:
        p.kind === 'boekhouding'
          ? state.agentTasks.map((t) => (t.agent === 'boekhouding' ? { ...t, status: 'klaar', stepLabel: 'ingeboekt' } : t))
          : state.agentTasks,
      log: [
        ...state.log,
        entry({ agent: p.agent, summary: `${p.title} · goedgekeurd`, level: 'naOk', where: p.where, outcome: 'goedgekeurd', traceId: p.traceId }),
      ],
    });
  },

  rejectProposal(id: string) {
    const p = state.proposals.find((x) => x.id === id);
    if (!p || p.status !== 'wacht') return;
    commit({
      ...state,
      proposals: state.proposals.map((x) => (x.id === id ? { ...x, status: 'afgewezen' } : x)),
      log: [
        ...state.log,
        entry({ agent: p.agent, summary: `${p.title} · afgewezen`, level: 'naOk', where: p.where, outcome: 'afgewezen', traceId: p.traceId }),
      ],
    });
  },

  /** Demo helper: put the approval queue back to its seed state. */
  resetProposals() {
    commit({
      ...state,
      proposals: structuredClone(seed.proposals),
      pullRequests: structuredClone(seed.pullRequests),
      agentTasks: structuredClone(seed.agentTasks),
    });
  },

  setLight(roomId: string, value: number) {
    const room = state.rooms.find((r) => r.id === roomId);
    if (!room) return;
    commit({
      ...state,
      rooms: state.rooms.map((r) => (r.id === roomId ? { ...r, light: value } : r)),
      log: [
        ...state.log,
        entry({ agent: 'huis', summary: `Licht ${room.name.toLowerCase()} op ${value}%`, level: 'zelf', where: 'lokaal', outcome: 'uitgevoerd', traceId: 'licht' }),
      ],
    });
  },

  setTarget(roomId: string, value: number, level: ActionLevel = 'zelf') {
    const room = state.rooms.find((r) => r.id === roomId);
    if (!room) return;
    commit({
      ...state,
      rooms: state.rooms.map((r) => (r.id === roomId ? { ...r, target: value } : r)),
      log: [
        ...state.log,
        entry({
          agent: 'huis',
          summary: `Verwarming ${room.name.toLowerCase()} naar ${value} °C`,
          level,
          where: 'lokaal',
          outcome: 'uitgevoerd',
          traceId: level === 'tenzijBezwaar' ? 'verwarming' : 'klimaat',
        }),
      ],
    });
  },

  updateTask(id: string, patch: Partial<AgentTask>) {
    commit({ ...state, agentTasks: state.agentTasks.map((t) => (t.id === id ? { ...t, ...patch } : t)) });
  },

  /** Stream the next scripted event of a task into its activity trace. */
  advanceTask(id: string) {
    const t = state.agentTasks.find((x) => x.id === id);
    if (!t || t.status !== 'bezig' || !t.upcoming?.length) return;
    const [next, ...rest] = t.upcoming;
    const step = next.step ?? t.step;
    const done = rest.length === 0 && step >= t.totalSteps;
    commit({
      ...state,
      agentTasks: state.agentTasks.map((x) =>
        x.id === id
          ? {
              ...x,
              upcoming: rest,
              events: [...x.events, next.event],
              step,
              stepLabel: done ? 'klaar' : next.stepLabel ?? x.stepLabel,
              status: done ? 'klaar' : x.status,
            }
          : x,
      ),
      log: done
        ? [...state.log, entry({ agent: t.agent, summary: `${t.title} · klaar`, level: 'zelf', where: t.where, outcome: 'uitgevoerd' })]
        : state.log,
    });
  },

  setGpu(gpu: Meta['gpu']) {
    commit({ ...state, meta: { ...state.meta, gpu } });
  },

  finishRender(id: string) {
    const r = state.renders.find((x) => x.id === id);
    if (!r || r.status === 'klaar') return;
    commit({
      ...state,
      meta: { ...state.meta, gpu: 'actief' },
      renders: state.renders.map((x) => (x.id === id ? { ...x, status: 'klaar' } : x)),
      agentTasks: state.agentTasks.map((t) =>
        t.agent === 'studio'
          ? { ...t, status: 'klaar', step: t.totalSteps, stepLabel: 'klaar', events: [...t.events, 'render klaar'] }
          : t,
      ),
      log: [...state.log, entry({ agent: 'studio', summary: `${r.title} · klaar`, level: 'zelf', where: r.where, outcome: 'uitgevoerd', traceId: 'render-landing' })],
    });
  },

  setSlack(id: string, status: SlackMessage['status']) {
    const m = state.slackMessages.find((x) => x.id === id);
    if (!m) return;
    commit({
      ...state,
      slackMessages: state.slackMessages.map((x) => (x.id === id ? { ...x, status } : x)),
      agentTasks:
        status === 'concept'
          ? state.agentTasks.map((t) =>
              t.agent === 'slack' ? { ...t, stepLabel: '1 vraag aan jou · concept klaar', events: [...t.events, 'concept-antwoord klaar'] } : t,
            )
          : state.agentTasks,
      log:
        status === 'geplaatst'
          ? [...state.log, entry({ agent: 'slack', summary: `Antwoord geplaatst in ${m.channel}`, level: 'naOk', where: 'lokaal', outcome: 'goedgekeurd', traceId: 'slack-bakker' })]
          : state.log,
    });
  },

  slackQuestionArrived(id: string) {
    const m = state.slackMessages.find((x) => x.id === id);
    if (!m) return;
    commit({
      ...state,
      agentTasks: state.agentTasks.map((t) =>
        t.agent === 'slack' ? { ...t, step: 2, stepLabel: '1 vraag aan jou gesignaleerd', events: [...t.events, '1 vraag aan jou gesignaleerd'] } : t,
      ),
    });
  },

  addCloudCost(euro: number) {
    if (state.meta.cloud.localOnly) return;
    commit({ ...state, meta: { ...state.meta, cloud: { ...state.meta.cloud, spent: state.meta.cloud.spent + euro } } });
  },

  /** Over budget: every running task moves to a local model for the rest of the day. */
  switchToLocal(spent: number) {
    commit({
      ...state,
      meta: { ...state.meta, cloud: { ...state.meta.cloud, spent, localOnly: true } },
      agentTasks: state.agentTasks.map((t) => (t.where === 'cloud' ? { ...t, where: 'lokaal', model: 'Qwen 32B', events: [...t.events, 'overgeschakeld op lokaal model'] } : t)),
      log: [...state.log, entry({ agent: 'systeem', summary: 'Cloudbudget 80%: rest van de dag lokaal', level: 'zelf', where: 'lokaal', outcome: 'uitgevoerd', traceId: 'cloud-budget' })],
    });
  },

  setDevice(id: string, status: string) {
    commit({ ...state, devices: state.devices.map((d) => (d.id === id ? { ...d, status } : d)) });
  },

  setMode(mode: HouseMode, by: Surface) {
    if (state.meta.mode === mode) return;
    commit({
      ...state,
      meta: { ...state.meta, mode },
      log: [...state.log, entry({ agent: 'systeem', summary: `Huismodus ${mode} (via ${by})`, level: 'zelf', where: 'lokaal', outcome: 'uitgevoerd' })],
    });
  },

  /* ─────────── Noodstop: one flag for the whole house ─────────── */

  stop(by: Surface) {
    if (state.meta.stopped) return;
    commit({
      ...state,
      meta: { ...state.meta, stopped: true, stoppedBy: by },
      log: [...state.log, entry({ agent: 'systeem', summary: `Noodstop via ${by}: alle agents gestopt`, level: 'zelf', where: 'lokaal', outcome: 'gestopt' })],
    });
  },

  resume(by: Surface) {
    if (!state.meta.stopped) return;
    commit({
      ...state,
      meta: { ...state.meta, stopped: false, stoppedBy: undefined },
      log: [...state.log, entry({ agent: 'systeem', summary: `Anna hervat via ${by}`, level: 'zelf', where: 'lokaal', outcome: 'uitgevoerd' })],
    });
  },

  setAgentPaused(agent: AgentId, paused: boolean) {
    const has = state.meta.pausedAgents.includes(agent);
    if (has === paused) return;
    commit({
      ...state,
      meta: {
        ...state.meta,
        pausedAgents: paused ? [...state.meta.pausedAgents, agent] : state.meta.pausedAgents.filter((a) => a !== agent),
      },
      log: [
        ...state.log,
        entry({ agent, summary: paused ? 'Agent gepauzeerd via beheer' : 'Agent weer actief', level: 'zelf', where: 'lokaal', outcome: paused ? 'gestopt' : 'uitgevoerd' }),
      ],
    });
  },

  /* ─────────── Settings & memory ─────────── */

  updateSettings(patch: Partial<AnnaSettingsState>, summary?: string) {
    commit({
      ...state,
      settings: { ...state.settings, ...patch },
      log: summary
        ? [...state.log, entry({ agent: 'systeem', summary, level: 'naOk', where: 'lokaal', outcome: 'goedgekeurd' })]
        : state.log,
    });
  },

  acceptMemory(id: string) {
    const p = state.memory.proposals.find((x) => x.id === id);
    if (!p) return;
    const m = state.memory;
    commit({
      ...state,
      memory: {
        ...m,
        proposals: m.proposals.filter((x) => x.id !== id),
        knowledge:
          p.kind === 'kennis'
            ? [{ id: `k-${id}`, topic: 'Voorkeuren', text: p.text, source: 'Voorstel, door jou bevestigd', learned: 'vandaag', uses: 0 }, ...m.knowledge]
            : m.knowledge,
        workflows:
          p.kind === 'werkwijze' ? [{ id: `w-${id}`, area: 'Gewoonte', rule: p.text, learned: 'vandaag, door jou bevestigd' }, ...m.workflows] : m.workflows,
      },
      log: [...state.log, entry({ agent: p.agent, summary: `Onthouden: ${p.text}`, level: 'naOk', where: 'lokaal', outcome: 'goedgekeurd' })],
    });
  },

  rejectMemory(id: string) {
    const p = state.memory.proposals.find((x) => x.id === id);
    if (!p) return;
    commit({
      ...state,
      memory: { ...state.memory, proposals: state.memory.proposals.filter((x) => x.id !== id) },
      log: [...state.log, entry({ agent: p.agent, summary: 'Geheugenvoorstel niet onthouden', level: 'naOk', where: 'lokaal', outcome: 'afgewezen' })],
    });
  },

  forgetMemory(kind: MemoryKind, id: string) {
    const list = state.memory[kind] as { id: string }[];
    if (!list.some((x) => x.id === id)) return;
    commit({
      ...state,
      memory: { ...state.memory, [kind]: list.filter((x) => x.id !== id) },
      log: [...state.log, entry({ agent: 'systeem', summary: 'Iets uit het geheugen vergeten', level: 'zelf', where: 'lokaal', outcome: 'uitgevoerd' })],
    });
  },

  /** Demo reset. Owner configuration and memory are not demo data, so they survive. */
  reset() {
    commit({ ...structuredClone(seed), settings: state.settings, memory: state.memory });
  },
};
